import { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPatch } from '../lib/apiClient';
import {
    fetchReferenceLibrary,
    mutateReference,
    toIsoFromLocalDateTimeInput,
    toLocalDateTimeInput,
    type BrandActivityReference,
    type BrandCelebrityReference,
    type BrandReferenceLibrary
} from '../lib/brandLibrary';

interface OfferSettings {
    default_offer: string;
    offer_variations: string[];
    auto_rotate_offer: boolean;
}

interface CelebrityFormState {
    name: string;
    startsAt: string;
    expiresAt: string;
    isActive: boolean;
    notes: string;
}

interface ActivityFormState {
    name: string;
    opinionLeaderName: string;
    startsAt: string;
    expiresAt: string;
    isEvergreen: boolean;
    isActive: boolean;
    notes: string;
}

interface CelebrityPerformance {
    name: string;
    assetCount: number;
    totalImpressions: number;
    totalClicks: number;
    totalConversions: number;
    totalCost: number;
    cpl: number;
    ctr: number;
    conversionRate: number;
    score: number;
}

interface TvShowPerformance {
    name: string;
    assetCount: number;
    totalImpressions: number;
    totalClicks: number;
    totalConversions: number;
    totalCost: number;
    cpl: number;
    ctr: number;
    conversionRate: number;
    score: number;
}

const EMPTY_CELEBRITY_FORM: CelebrityFormState = {
    name: '',
    startsAt: '',
    expiresAt: '',
    isActive: true,
    notes: ''
};

const EMPTY_ACTIVITY_FORM: ActivityFormState = {
    name: '',
    opinionLeaderName: '',
    startsAt: '',
    expiresAt: '',
    isEvergreen: false,
    isActive: true,
    notes: ''
};

function getStatusLabel(status: string): string {
    switch (status) {
        case 'active':
            return '可用中';
        case 'expiring_soon':
            return '即將到期';
        case 'expired':
            return '已過期';
        case 'scheduled':
            return '尚未開始';
        case 'inactive':
            return '已停用';
        case 'evergreen':
            return '常態保底';
        default:
            return status;
    }
}

function getStatusColor(status: string): string {
    switch (status) {
        case 'active':
        case 'evergreen':
            return 'var(--color-success)';
        case 'expiring_soon':
        case 'scheduled':
            return 'var(--color-warning)';
        case 'expired':
        case 'inactive':
            return 'var(--color-danger)';
        default:
            return 'var(--color-text-secondary)';
    }
}

function formatDateTime(iso: string | null): string {
    if (!iso) return '未設定';
    return new Date(iso).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function StatusBadge({ status }: { status: string }) {
    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0.2rem 0.55rem',
            borderRadius: '999px',
            fontSize: '0.75rem',
            fontWeight: 700,
            color: 'white',
            background: getStatusColor(status)
        }}>
            {getStatusLabel(status)}
        </span>
    );
}

export function Settings() {
    const [loading, setLoading] = useState(true);
    const [savingOffer, setSavingOffer] = useState(false);
    const [mutating, setMutating] = useState(false);
    const [settings, setSettings] = useState<OfferSettings>({
        default_offer: '最高折$15,000',
        offer_variations: [],
        auto_rotate_offer: false
    });
    const [library, setLibrary] = useState<BrandReferenceLibrary | null>(null);
    const [newVariation, setNewVariation] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const [celebrityForm, setCelebrityForm] = useState<CelebrityFormState>(EMPTY_CELEBRITY_FORM);
    const [activityForm, setActivityForm] = useState<ActivityFormState>(EMPTY_ACTIVITY_FORM);
    const [editingCelebrityId, setEditingCelebrityId] = useState<number | null>(null);
    const [editingActivityId, setEditingActivityId] = useState<number | null>(null);
    const [fallbackPool, setFallbackPool] = useState<CelebrityPerformance[]>([]);
    const [loadingFallback, setLoadingFallback] = useState(false);
    const [refreshingFallback, setRefreshingFallback] = useState(false);
    const [tvShowFallbackPool, setTvShowFallbackPool] = useState<TvShowPerformance[]>([]);
    const [loadingTvShowFallback, setLoadingTvShowFallback] = useState(false);
    const [refreshingTvShowFallback, setRefreshingTvShowFallback] = useState(false);

    useEffect(() => {
        void loadAll();
        void loadFallbackPool();
        void loadTvShowFallbackPool();
    }, []);

    async function loadAll() {
        try {
            setLoading(true);
            setError('');
            const [settingsRes, referenceLibrary] = await Promise.all([
                apiGet('/api/settings').then((res) => res.json() as Promise<{ success: boolean; settings?: OfferSettings; error?: string }>),
                fetchReferenceLibrary()
            ]);

            if (!settingsRes.success || !settingsRes.settings) {
                throw new Error(settingsRes.error || '無法載入優惠設定');
            }

            setSettings({
                default_offer: settingsRes.settings.default_offer || '最高折$15,000',
                offer_variations: settingsRes.settings.offer_variations || [],
                auto_rotate_offer: Boolean(settingsRes.settings.auto_rotate_offer)
            });
            setLibrary(referenceLibrary);
        } catch (err) {
            setError(err instanceof Error ? err.message : '載入品牌資料庫失敗');
        } finally {
            setLoading(false);
        }
    }

    async function loadFallbackPool() {
        try {
            setLoadingFallback(true);
            const res = await apiGet('/api/analytics/celebrity-performance?limit=5');
            const data = await res.json() as { success: boolean; celebrities?: CelebrityPerformance[]; error?: string };
            
            if (data.success && data.celebrities) {
                setFallbackPool(data.celebrities);
            }
        } catch (err) {
            console.error('載入 fallback pool 失敗', err);
        } finally {
            setLoadingFallback(false);
        }
    }

    async function refreshFallbackPool() {
        try {
            setRefreshingFallback(true);
            setError('');
            setSuccessMessage('');
            const res = await apiPost('/api/analytics/refresh-fallback-pool');
            const data = await res.json() as { success: boolean; celebrities?: string[]; performances?: CelebrityPerformance[]; error?: string };
            
            if (data.success && data.performances) {
                setFallbackPool(data.performances);
                setSuccessMessage(`Fallback pool 已更新：${data.celebrities?.join('、') || ''}`);
            } else {
                throw new Error(data.error || '更新失敗');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : '更新 fallback pool 失敗');
        } finally {
            setRefreshingFallback(false);
        }
    }

    async function loadTvShowFallbackPool() {
        try {
            setLoadingTvShowFallback(true);
            const res = await apiGet('/api/analytics/tv-show-performance?limit=5');
            const data = await res.json() as { success: boolean; tvShows?: TvShowPerformance[]; error?: string };
            
            if (data.success && data.tvShows) {
                setTvShowFallbackPool(data.tvShows);
            }
        } catch (err) {
            console.error('載入節目 fallback pool 失敗', err);
        } finally {
            setLoadingTvShowFallback(false);
        }
    }

    async function refreshTvShowFallbackPool() {
        try {
            setRefreshingTvShowFallback(true);
            setError('');
            setSuccessMessage('');
            const res = await apiPost('/api/analytics/refresh-tv-show-fallback-pool');
            const data = await res.json() as { success: boolean; tvShows?: string[]; performances?: TvShowPerformance[]; error?: string };
            
            if (data.success && data.performances) {
                setTvShowFallbackPool(data.performances);
                setSuccessMessage(`節目 fallback pool 已更新：${data.tvShows?.join('、') || ''}`);
            } else {
                throw new Error(data.error || '更新失敗');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : '更新節目 fallback pool 失敗');
        } finally {
            setRefreshingTvShowFallback(false);
        }
    }

    async function handleSaveOffers() {
        try {
            setSavingOffer(true);
            setError('');
            setSuccessMessage('');
            const res = await apiPatch('/api/settings', settings);
            const data = await res.json() as { success: boolean; error?: string };
            if (!res.ok || !data.success) {
                throw new Error(data.error || '優惠設定儲存失敗');
            }
            setSuccessMessage('優惠設定已更新');
        } catch (err) {
            setError(err instanceof Error ? err.message : '優惠設定儲存失敗');
        } finally {
            setSavingOffer(false);
        }
    }

    function addVariation() {
        const value = newVariation.trim();
        if (!value || settings.offer_variations.includes(value)) return;
        setSettings({
            ...settings,
            offer_variations: [...settings.offer_variations, value]
        });
        setNewVariation('');
    }

    function removeVariation(index: number) {
        setSettings({
            ...settings,
            offer_variations: settings.offer_variations.filter((_, i) => i !== index)
        });
    }

    function beginCelebrityEdit(item: BrandCelebrityReference) {
        setEditingCelebrityId(item.id);
        setCelebrityForm({
            name: item.name,
            startsAt: toLocalDateTimeInput(item.startsAt),
            expiresAt: toLocalDateTimeInput(item.expiresAt),
            isActive: item.isActive,
            notes: item.notes || ''
        });
    }

    function beginActivityEdit(item: BrandActivityReference) {
        setEditingActivityId(item.id);
        setActivityForm({
            name: item.name,
            opinionLeaderName: item.opinionLeaderName || '',
            startsAt: toLocalDateTimeInput(item.startsAt),
            expiresAt: toLocalDateTimeInput(item.expiresAt),
            isEvergreen: item.isEvergreen,
            isActive: item.isActive,
            notes: item.notes || ''
        });
    }

    function resetCelebrityForm() {
        setEditingCelebrityId(null);
        setCelebrityForm(EMPTY_CELEBRITY_FORM);
    }

    function resetActivityForm() {
        setEditingActivityId(null);
        setActivityForm(EMPTY_ACTIVITY_FORM);
    }

    async function submitCelebrity() {
        try {
            setMutating(true);
            setError('');
            setSuccessMessage('');
            await mutateReference(
                editingCelebrityId ? `/api/settings/reference-celebrities/${editingCelebrityId}` : '/api/settings/reference-celebrities',
                editingCelebrityId ? 'PATCH' : 'POST',
                {
                    name: celebrityForm.name,
                    startsAt: toIsoFromLocalDateTimeInput(celebrityForm.startsAt),
                    expiresAt: toIsoFromLocalDateTimeInput(celebrityForm.expiresAt),
                    isActive: celebrityForm.isActive,
                    notes: celebrityForm.notes || null
                }
            );
            setSuccessMessage(editingCelebrityId ? '名人素材已更新' : '名人素材已新增');
            resetCelebrityForm();
            setLibrary(await fetchReferenceLibrary());
        } catch (err) {
            setError(err instanceof Error ? err.message : '名人素材儲存失敗');
        } finally {
            setMutating(false);
        }
    }

    async function submitActivity() {
        try {
            setMutating(true);
            setError('');
            setSuccessMessage('');
            await mutateReference(
                editingActivityId ? `/api/settings/reference-activities/${editingActivityId}` : '/api/settings/reference-activities',
                editingActivityId ? 'PATCH' : 'POST',
                {
                    name: activityForm.name,
                    opinionLeaderName: activityForm.opinionLeaderName || null,
                    startsAt: activityForm.isEvergreen ? null : toIsoFromLocalDateTimeInput(activityForm.startsAt),
                    expiresAt: activityForm.isEvergreen ? null : toIsoFromLocalDateTimeInput(activityForm.expiresAt),
                    isEvergreen: activityForm.isEvergreen,
                    isActive: activityForm.isActive,
                    notes: activityForm.notes || null
                }
            );
            setSuccessMessage(editingActivityId ? '活動素材已更新' : '活動素材已新增');
            resetActivityForm();
            setLibrary(await fetchReferenceLibrary());
        } catch (err) {
            setError(err instanceof Error ? err.message : '活動素材儲存失敗');
        } finally {
            setMutating(false);
        }
    }

    async function toggleCelebrity(item: BrandCelebrityReference) {
        try {
            setMutating(true);
            await mutateReference(`/api/settings/reference-celebrities/${item.id}`, 'PATCH', {
                isActive: !item.isActive
            });
            setLibrary(await fetchReferenceLibrary());
        } catch (err) {
            setError(err instanceof Error ? err.message : '更新失敗');
        } finally {
            setMutating(false);
        }
    }

    async function toggleActivity(item: BrandActivityReference) {
        try {
            setMutating(true);
            await mutateReference(`/api/settings/reference-activities/${item.id}`, 'PATCH', {
                isActive: !item.isActive
            });
            setLibrary(await fetchReferenceLibrary());
        } catch (err) {
            setError(err instanceof Error ? err.message : '更新失敗');
        } finally {
            setMutating(false);
        }
    }

    async function deleteCelebrity(item: BrandCelebrityReference) {
        try {
            setMutating(true);
            await mutateReference(`/api/settings/reference-celebrities/${item.id}`, 'DELETE');
            if (editingCelebrityId === item.id) resetCelebrityForm();
            setLibrary(await fetchReferenceLibrary());
        } catch (err) {
            setError(err instanceof Error ? err.message : '刪除失敗');
        } finally {
            setMutating(false);
        }
    }

    async function deleteActivity(item: BrandActivityReference) {
        try {
            setMutating(true);
            await mutateReference(`/api/settings/reference-activities/${item.id}`, 'DELETE');
            if (editingActivityId === item.id) resetActivityForm();
            setLibrary(await fetchReferenceLibrary());
        } catch (err) {
            setError(err instanceof Error ? err.message : '刪除失敗');
        } finally {
            setMutating(false);
        }
    }

    if (loading) {
        return <div className="loading"><div className="spinner"></div></div>;
    }

    return (
        <div>
            <h2 style={{ marginBottom: '1.5rem' }}>品牌資料庫</h2>

            {error && (
                <div className="card" style={{ marginBottom: '1rem', borderColor: 'var(--color-danger)' }}>
                    {error}
                </div>
            )}
            {successMessage && (
                <div className="card" style={{ marginBottom: '1rem', borderColor: 'var(--color-success)' }}>
                    {successMessage}
                </div>
            )}

            {library && (library.summary.expiredCelebrities > 0 || library.summary.expiredActivities > 0 || library.summary.expiringCelebrities > 0 || library.summary.expiringActivities > 0) && (
                <div className="card" style={{ marginBottom: '1.5rem', borderColor: 'var(--color-warning)' }}>
                    <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>品牌資料庫提醒</div>
                    {library.summary.expiredCelebrities > 0 && <div>已過期名人 {library.summary.expiredCelebrities} 筆，系統不會使用，請刪除或更新。</div>}
                    {library.summary.expiredActivities > 0 && <div>已過期活動 {library.summary.expiredActivities} 筆，系統不會使用，請刪除或更新。</div>}
                    {library.summary.expiringCelebrities > 0 && <div>七天內到期名人 {library.summary.expiringCelebrities} 筆，請提前續約或停用。</div>}
                    {library.summary.expiringActivities > 0 && <div>七天內到期活動 {library.summary.expiringActivities} 筆，請提前調整檔期。</div>}
                </div>
            )}

            <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3 className="card-title" style={{ marginBottom: '1rem' }}>優惠設定</h3>
                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-text-secondary)' }}>預設優惠文案</label>
                    <input
                        type="text"
                        value={settings.default_offer}
                        onChange={(e) => setSettings({ ...settings, default_offer: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-text)' }}
                    />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-text-secondary)' }}>優惠變體</label>
                    {settings.offer_variations.map((variation, index) => (
                        <div key={`${variation}-${index}`} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <div style={{ flex: 1, padding: '0.65rem', background: 'var(--color-bg-secondary)', borderRadius: '8px' }}>{variation}</div>
                            <button className="btn btn-outline" onClick={() => removeVariation(index)}>刪除</button>
                        </div>
                    ))}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                            type="text"
                            value={newVariation}
                            onChange={(e) => setNewVariation(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                                    e.preventDefault();
                                    addVariation();
                                }
                            }}
                            style={{ flex: 1, padding: '0.75rem', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-text)' }}
                            placeholder="例：春季限定現折 1 萬 5"
                        />
                        <button className="btn btn-outline" onClick={addVariation}>新增</button>
                    </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <input
                        type="checkbox"
                        checked={settings.auto_rotate_offer}
                        onChange={(e) => setSettings({ ...settings, auto_rotate_offer: e.target.checked })}
                    />
                    <span>自動輪替優惠</span>
                </label>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary" onClick={handleSaveOffers} disabled={savingOffer}>
                        {savingOffer ? '儲存中...' : '儲存優惠設定'}
                    </button>
                </div>
            </div>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3 className="card-title" style={{ marginBottom: '1rem' }}>名人素材</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '1rem' }}>
                    <div>
                        <div style={{ fontWeight: 700, marginBottom: '0.75rem' }}>
                            {editingCelebrityId ? '編輯名人素材' : '新增名人素材'}
                        </div>
                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                            <input
                                type="text"
                                value={celebrityForm.name}
                                onChange={(e) => setCelebrityForm({ ...celebrityForm, name: e.target.value })}
                                placeholder="名人名稱"
                                style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', color: 'var(--color-text)' }}
                            />
                            <input
                                type="datetime-local"
                                value={celebrityForm.startsAt}
                                onChange={(e) => setCelebrityForm({ ...celebrityForm, startsAt: e.target.value })}
                                style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', color: 'var(--color-text)' }}
                            />
                            <input
                                type="datetime-local"
                                value={celebrityForm.expiresAt}
                                onChange={(e) => setCelebrityForm({ ...celebrityForm, expiresAt: e.target.value })}
                                style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', color: 'var(--color-text)' }}
                            />
                            <textarea
                                rows={3}
                                value={celebrityForm.notes}
                                onChange={(e) => setCelebrityForm({ ...celebrityForm, notes: e.target.value })}
                                placeholder="備註"
                                style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', color: 'var(--color-text)', resize: 'vertical' }}
                            />
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                    type="checkbox"
                                    checked={celebrityForm.isActive}
                                    onChange={(e) => setCelebrityForm({ ...celebrityForm, isActive: e.target.checked })}
                                />
                                <span>啟用</span>
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button className="btn btn-primary" onClick={submitCelebrity} disabled={mutating}>
                                    {editingCelebrityId ? '更新名人素材' : '新增名人素材'}
                                </button>
                                {editingCelebrityId && (
                                    <button className="btn btn-outline" onClick={resetCelebrityForm}>取消編輯</button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {library?.celebrities.all.map((item) => (
                            <div key={item.id} style={{ padding: '0.9rem', border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-bg-secondary)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                    <div style={{ fontWeight: 700 }}>{item.name}</div>
                                    <StatusBadge status={item.status} />
                                </div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                                    生效：{formatDateTime(item.startsAt)} / 到期：{formatDateTime(item.expiresAt)}
                                </div>
                                {item.notes && (
                                    <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                                        備註：{item.notes}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <button className="btn btn-outline" onClick={() => beginCelebrityEdit(item)}>編輯</button>
                                    <button className="btn btn-outline" onClick={() => toggleCelebrity(item)}>{item.isActive ? '停用' : '啟用'}</button>
                                    <button className="btn btn-outline" onClick={() => deleteCelebrity(item)}>刪除</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="card">
                <h3 className="card-title" style={{ marginBottom: '1rem' }}>活動素材</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '1rem' }}>
                    <div>
                        <div style={{ fontWeight: 700, marginBottom: '0.75rem' }}>
                            {editingActivityId ? '編輯活動素材' : '新增活動素材'}
                        </div>
                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                            <input
                                type="text"
                                value={activityForm.name}
                                onChange={(e) => setActivityForm({ ...activityForm, name: e.target.value })}
                                placeholder="活動名稱"
                                style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', color: 'var(--color-text)' }}
                            />
                            <input
                                type="text"
                                value={activityForm.opinionLeaderName}
                                onChange={(e) => setActivityForm({ ...activityForm, opinionLeaderName: e.target.value })}
                                placeholder="意見領袖名稱（選填，用於活動特式化 H1-1~2）"
                                style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', color: 'var(--color-text)' }}
                            />
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                    type="checkbox"
                                    checked={activityForm.isEvergreen}
                                    onChange={(e) => setActivityForm({ ...activityForm, isEvergreen: e.target.checked })}
                                />
                                <span>常態保底活動</span>
                            </label>
                            {!activityForm.isEvergreen && (
                                <>
                                    <input
                                        type="datetime-local"
                                        value={activityForm.startsAt}
                                        onChange={(e) => setActivityForm({ ...activityForm, startsAt: e.target.value })}
                                        style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', color: 'var(--color-text)' }}
                                    />
                                    <input
                                        type="datetime-local"
                                        value={activityForm.expiresAt}
                                        onChange={(e) => setActivityForm({ ...activityForm, expiresAt: e.target.value })}
                                        style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', color: 'var(--color-text)' }}
                                    />
                                </>
                            )}
                            <textarea
                                rows={3}
                                value={activityForm.notes}
                                onChange={(e) => setActivityForm({ ...activityForm, notes: e.target.value })}
                                placeholder="備註"
                                style={{ padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', color: 'var(--color-text)', resize: 'vertical' }}
                            />
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                    type="checkbox"
                                    checked={activityForm.isActive}
                                    onChange={(e) => setActivityForm({ ...activityForm, isActive: e.target.checked })}
                                />
                                <span>啟用</span>
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button className="btn btn-primary" onClick={submitActivity} disabled={mutating}>
                                    {editingActivityId ? '更新活動素材' : '新增活動素材'}
                                </button>
                                {editingActivityId && (
                                    <button className="btn btn-outline" onClick={resetActivityForm}>取消編輯</button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {library?.activities.all.map((item) => (
                            <div key={item.id} style={{ padding: '0.9rem', border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-bg-secondary)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                    <div style={{ fontWeight: 700 }}>{item.name}</div>
                                    <StatusBadge status={item.status} />
                                </div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                                    開始：{formatDateTime(item.startsAt)} / 到期：{formatDateTime(item.expiresAt)}
                                </div>
                                {item.opinionLeaderName && (
                                    <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                                        意見領袖：{item.opinionLeaderName}
                                    </div>
                                )}
                                {item.notes && (
                                    <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                                        備註：{item.notes}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <button className="btn btn-outline" onClick={() => beginActivityEdit(item)}>編輯</button>
                                    <button className="btn btn-outline" onClick={() => toggleActivity(item)}>{item.isActive ? '停用' : '啟用'}</button>
                                    <button className="btn btn-outline" onClick={() => deleteActivity(item)}>刪除</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="card" style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 className="card-title">名人保底池（Fallback Pool）</h3>
                    <button 
                        className="btn btn-outline" 
                        onClick={refreshFallbackPool} 
                        disabled={refreshingFallback}
                    >
                        {refreshingFallback ? '更新中...' : '重新分析'}
                    </button>
                </div>
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--color-bg-secondary)', borderRadius: '8px', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                    <strong>說明：</strong>從歷史文案紀錄中分析表現最好的前 5 名名人（已排除莊碧玉），作為常態保底池。
                    排序使用綜合評分：<strong>分數 = (轉換數 × 10) - (CPL × 0.05)</strong>，優先考量名單數量。
                    當「見證代言特式化」需要名人但沒有手動指定或啟用的名人時，系統會自動使用這些名人。
                </div>
                {loadingFallback ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>載入中...</div>
                ) : fallbackPool.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                        尚無資料，請點擊「重新分析」按鈕
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {fallbackPool.map((celeb, index) => (
                            <div key={celeb.name} style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-bg-secondary)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span style={{ 
                                            display: 'inline-flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            width: '1.5rem', 
                                            height: '1.5rem', 
                                            borderRadius: '50%', 
                                            background: 'var(--color-primary)', 
                                            color: 'white', 
                                            fontSize: '0.75rem', 
                                            fontWeight: 700 
                                        }}>
                                            {index + 1}
                                        </span>
                                        <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{celeb.name}</span>
                                    </div>
                                    <span style={{ 
                                        padding: '0.25rem 0.6rem', 
                                        borderRadius: '999px', 
                                        background: 'var(--color-success)', 
                                        color: 'white', 
                                        fontSize: '0.75rem', 
                                        fontWeight: 700 
                                    }}>
                                        分數 {celeb.score.toFixed(1)}
                                    </span>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>文案數</div>
                                        <div style={{ fontWeight: 600 }}>{celeb.assetCount}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>轉換數</div>
                                        <div style={{ fontWeight: 600 }}>{celeb.totalConversions.toFixed(1)}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>CPL</div>
                                        <div style={{ fontWeight: 600 }}>${celeb.cpl.toFixed(0)}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>曝光數</div>
                                        <div style={{ fontWeight: 600 }}>{celeb.totalImpressions.toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>點擊數</div>
                                        <div style={{ fontWeight: 600 }}>{celeb.totalClicks.toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>CTR</div>
                                        <div style={{ fontWeight: 600 }}>{(celeb.ctr * 100).toFixed(2)}%</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="card" style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 className="card-title">節目保底池（TV Show Fallback Pool）</h3>
                    <button 
                        className="btn btn-outline" 
                        onClick={refreshTvShowFallbackPool} 
                        disabled={refreshingTvShowFallback}
                    >
                        {refreshingTvShowFallback ? '更新中...' : '重新分析'}
                    </button>
                </div>
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--color-bg-secondary)', borderRadius: '8px', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                    <strong>說明：</strong>從歷史文案紀錄中分析表現最好的前 5 個節目，作為常態保底池。
                    排序使用綜合評分：<strong>分數 = (轉換數 × 10) - (CPL × 0.05)</strong>，優先考量名單數量。
                    當「見證代言特式化」需要節目但沒有手動指定或啟用的節目時，系統會自動使用這些節目。
                </div>
                {loadingTvShowFallback ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>載入中...</div>
                ) : tvShowFallbackPool.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                        尚無資料，請點擊「重新分析」按鈕
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {tvShowFallbackPool.map((show, index) => (
                            <div key={show.name} style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '10px', background: 'var(--color-bg-secondary)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span style={{ 
                                            display: 'inline-flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            width: '1.5rem', 
                                            height: '1.5rem', 
                                            borderRadius: '50%', 
                                            background: 'var(--color-primary)', 
                                            color: 'white', 
                                            fontSize: '0.75rem', 
                                            fontWeight: 700 
                                        }}>
                                            {index + 1}
                                        </span>
                                        <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{show.name}</span>
                                    </div>
                                    <span style={{ 
                                        padding: '0.25rem 0.6rem', 
                                        borderRadius: '999px', 
                                        background: 'var(--color-info)', 
                                        color: 'white', 
                                        fontSize: '0.75rem', 
                                        fontWeight: 700 
                                    }}>
                                        分數 {show.score.toFixed(1)}
                                    </span>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>文案數</div>
                                        <div style={{ fontWeight: 600 }}>{show.assetCount}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>轉換數</div>
                                        <div style={{ fontWeight: 600 }}>{show.totalConversions.toFixed(1)}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>CPL</div>
                                        <div style={{ fontWeight: 600 }}>${show.cpl.toFixed(0)}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>曝光數</div>
                                        <div style={{ fontWeight: 600 }}>{show.totalImpressions.toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>點擊數</div>
                                        <div style={{ fontWeight: 600 }}>{show.totalClicks.toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>CTR</div>
                                        <div style={{ fontWeight: 600 }}>{(show.ctr * 100).toFixed(2)}%</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
